import React from 'react';
import { Box, Button, alpha, useTheme } from '@mui/material';
import WorkspaceStatusBanner from '../../../shared/ui/workflow/WorkspaceStatusBanner';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import { borderRadius, layoutGap } from '../../../theme/theme';
import type { MappingProjectReadinessResult } from '../lib/mappingProjectReadiness';

interface MappingReadinessAlertProps {
  readiness: MappingProjectReadinessResult;
  modelDataStale?: boolean;
  onRefreshModelData?: () => void;
  onRepairSetup?: () => void | Promise<void>;
  onOpenQuickFill?: () => void;
  onReinitiateWorkflow: () => void;
}

function shouldShowMappingReadinessAlert(readiness: MappingProjectReadinessResult): boolean {
  return (
    readiness.status !== 'ready' &&
    readiness.status !== 'unavailable' &&
    readiness.status !== 'repairing' &&
    Boolean(readiness.message)
  );
}

function shouldShowRetrySetupButton(readiness: MappingProjectReadinessResult): boolean {
  return (
    readiness.status === 'blocked' &&
    readiness.blockReason !== 'profileConfirmation' &&
    readiness.blockReason !== 'familyRepairRequired'
  );
}

export default function MappingReadinessAlert({
  readiness,
  modelDataStale = false,
  onRefreshModelData,
  onRepairSetup,
  onOpenQuickFill,
  onReinitiateWorkflow,
}: MappingReadinessAlertProps): React.ReactElement | null {
  const theme = useTheme();

  const showReadinessAlert = shouldShowMappingReadinessAlert(readiness);
  if (!showReadinessAlert && !modelDataStale) {
    return null;
  }

  const tone = readiness.status === 'blocked' ? 'warning' : 'info';
  const isChecking = readiness.status === 'checking';

  const primaryActionSx = {
    minWidth: 'auto',
    minHeight: 22,
    px: 1,
    py: 0.15,
    fontSize: '0.68rem',
    fontWeight: 800,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    borderRadius: `${borderRadius.sm}px`,
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
    '&:hover': {
      boxShadow: `0 3px 12px ${alpha(theme.palette.primary.main, 0.45)}`,
    },
  } as const;

  const secondaryActionSx = {
    minWidth: 'auto',
    minHeight: 22,
    px: 0.5,
    py: 0.15,
    fontSize: '0.66rem',
    fontWeight: 650,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    color: 'inherit',
    opacity: 0.78,
    '&:hover': {
      opacity: 1,
      backgroundColor: alpha(theme.palette.common.black, 0.05),
    },
  } as const;

  const action =
    readiness.status === 'blocked' ? (
      <Box sx={{ display: 'flex', gap: layoutGap.dense, alignItems: 'center' }}>
        {readiness.blockReason === 'profileConfirmation' && onRepairSetup ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onRepairSetup()}
            sx={primaryActionSx}
            data-testid="mapping-repair-setup-button"
          >
            Create missing Revit parameters
          </Button>
        ) : null}
        {readiness.blockReason === 'familyRepairRequired' && onOpenQuickFill ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onOpenQuickFill()}
            sx={primaryActionSx}
            data-testid="mapping-open-quickfill-button"
          >
            Update families in Quick Fill
          </Button>
        ) : null}
        {shouldShowRetrySetupButton(readiness) ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={readiness.retry}
            sx={primaryActionSx}
          >
            Check project parameters
          </Button>
        ) : null}
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={onReinitiateWorkflow}
          sx={secondaryActionSx}
        >
          Revise setup
        </Button>
      </Box>
    ) : undefined;

  const staleBanner = modelDataStale ? (
    <WorkspaceStatusBanner
      tone="info"
      action={
        onRefreshModelData ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onRefreshModelData}
            sx={primaryActionSx}
            data-testid="mapping-refresh-model-data-button"
          >
            Refresh Revit data
          </Button>
        ) : undefined
      }
      dataTestId="mapping-model-stale-banner"
    >
      The active Revit model has changed. Refresh the Revit data to check readiness again.
    </WorkspaceStatusBanner>
  ) : null;

  if (!showReadinessAlert) {
    return staleBanner;
  }

  return (
    <>
      {staleBanner}
      <WorkspaceStatusBanner
        tone={tone}
        icon={
          isChecking ? (
            <OperationProgressIndicator
              size={13}
              ariaLabel="Checking project parameters"
              sx={{ display: 'block' }}
            />
          ) : undefined
        }
        action={action}
        dataTestId="mapping-readiness-banner"
      >
        {readiness.message}
      </WorkspaceStatusBanner>
    </>
  );
}
